import { useState, useEffect, useCallback } from 'react';
import { type Shipment, type ShipmentStatus, STATUS_CONFIG } from '../../../mocks/shipments';
import { fetchAllShipments, updateShipment, deleteShipment } from '../../../lib/shipmentsService';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../hooks/useToast';
import AdminEditShipmentModal from './AdminEditShipmentModal';
import AdminShipmentDetailPanel from './AdminShipmentDetailPanel';

interface StatusUpdateModal {
  shipment: Shipment;
  newStatus: ShipmentStatus;
  note: string;
  reason: string;
  currentLocation: string;
}

const EDGE_FN_URL = 'https://ttvxezxhcdxwsajhcjtl.supabase.co/functions/v1/send-notification';

async function sendNotification(shipment: Shipment, newStatus: ShipmentStatus, statusLabel: string, currentLocation: string, reason: string) {
  const email = shipment.receiver?.email;
  if (!email) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({
        trackingId: shipment.id,
        receiverEmail: email,
        receiverName: shipment.receiver.name,
        newStatus,
        statusLabel,
        currentLocation: currentLocation || null,
        reason: reason || null,
        estimatedDelivery: shipment.estimatedDelivery,
        origin: `${shipment.origin.city}, ${shipment.origin.country}`,
        destination: `${shipment.destination.city}, ${shipment.destination.country}`,
        siteUrl: window.location.hostname.includes('readdy') || window.location.hostname === 'localhost'
          ? 'https://swift-tracks.com'
          : window.location.origin,
      }),
    });
  } catch {
    // notification failure is non-blocking
  }
}

// ... existing code ...

export default function AdminShipmentsTab() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');
  const [timelineShipment, setTimelineShipment] = useState<Shipment | null>(null);
  const [newEventStatus, setNewEventStatus] = useState<ShipmentStatus>('in_transit');
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventDesc, setNewEventDesc] = useState('');
  const [statusModal, setStatusModal] = useState<StatusUpdateModal | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<ShipmentStatus>('in_transit');
  const [bulkNote, setBulkNote] = useState('');
  const [showBulkNoteInput, setShowBulkNoteInput] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editShipment, setEditShipment] = useState<Shipment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Shipment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { addToast } = useToast();
  const [detailShipment, setDetailShipment] = useState<Shipment | null>(null);

  const loadShipments = useCallback(async () => {
    setDbLoading(true);
    const data = await fetchAllShipments();
    setShipments(data);
    setDbLoading(false);
  }, []);

  useEffect(() => { loadShipments(); }, [loadShipments]);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await deleteShipment(deleteTarget.id);
    setDeleting(false);
    if (error) {
      addToast({ type: 'error', title: 'Delete Failed', message: error });
    } else {
      addToast({ type: 'success', title: 'Shipment Deleted', message: `${deleteTarget.id} has been removed` });
      setDeleteTarget(null);
      loadShipments();
    }
  };

  const filtered = shipments.filter((s) => {
    const q = search.toLowerCase();
    const matchQ = !q || s.id.toLowerCase().includes(q) || s.sender.name.toLowerCase().includes(q) || s.receiver.name.toLowerCase().includes(q) || s.origin.city.toLowerCase().includes(q) || s.destination.city.toLowerCase().includes(q);
    return matchQ && (!filterStatus || s.status === filterStatus) && (!filterService || s.service === filterService);
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selectedIds.has(s.id));
  const someSelected = selectedIds.size > 0;
  const toggleSelectAll = () => { if (allFilteredSelected) setSelectedIds(new Set()); else setSelectedIds(new Set(filtered.map((s) => s.id))); };
  const toggleSelect = (id: string) => { setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };

  const applyBulkUpdate = async () => {
    const count = selectedIds.size;
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const ids = [...selectedIds];
    await Promise.all(ids.map((id) => {
      const s = shipments.find((sh) => sh.id === id);
      if (!s) return Promise.resolve();
      const newTimeline = [...s.timeline, { status: bulkStatus, timestamp: now, location: s.destination.city, description: `Bulk update → ${STATUS_CONFIG[bulkStatus].label}${bulkNote.trim() ? ` — ${bulkNote.trim()}` : ''}` }];
      return updateShipment(id, { status: bulkStatus, note: bulkNote.trim() || s.note, timeline: newTimeline });
    }));
    addToast({ type: 'success', title: 'Bulk Update Applied', message: `${count} shipment${count !== 1 ? 's' : ''} updated` });
    setSelectedIds(new Set()); setBulkNote(''); setShowBulkNoteInput(false); loadShipments();
  };

  const openStatusModal = (s: Shipment) => setStatusModal({ shipment: s, newStatus: s.status, note: s.note || '', reason: s.reason || '', currentLocation: s.currentLocation?.city || '' });

  const saveStatusUpdate = async () => {
    if (!statusModal) return;
    const { shipment, newStatus, note, reason, currentLocation } = statusModal;
    const statusChanged = newStatus !== shipment.status;
    const locLabel = currentLocation.trim() || shipment.destination.city;
    const newTimeline = statusChanged
      ? [...shipment.timeline, { status: newStatus, timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '), location: locLabel, description: `Status updated to ${STATUS_CONFIG[newStatus].label}${reason.trim() ? ` — ${reason.trim()}` : ''}` }]
      : shipment.timeline;

    const { error } = await updateShipment(shipment.id, {
      status: newStatus,
      note: note.trim() || undefined,
      reason: reason.trim() || undefined,
      timeline: newTimeline,
      currentLocationStr: currentLocation.trim() || undefined,
    });

    if (error) { addToast({ type: 'error', title: 'Update Failed', message: error }); return; }

    // Send email notification
    await sendNotification(shipment, newStatus, STATUS_CONFIG[newStatus].label, currentLocation.trim(), reason.trim());

    addToast({ type: 'success', title: 'Shipment Updated', message: `${shipment.id} → ${STATUS_CONFIG[newStatus].label}${shipment.receiver?.email ? ' · Notification sent' : ''}` });
    setStatusModal(null);
    loadShipments();
  };

  const addTimelineEvent = async () => {
    if (!timelineShipment || !newEventLocation.trim() || !newEventDesc.trim()) return;
    const newTimeline = [...timelineShipment.timeline, { status: newEventStatus, timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '), location: newEventLocation, description: newEventDesc }];
    const { error } = await updateShipment(timelineShipment.id, { status: newEventStatus, timeline: newTimeline, currentLocationStr: newEventLocation });
    if (!error) { addToast({ type: 'success', title: 'Timeline Updated', message: `Event added to ${timelineShipment.id}` }); setNewEventLocation(''); setNewEventDesc(''); loadShipments(); }
  };

  const services = [...new Set(shipments.map((s) => s.service))];
  const modalCfg = statusModal ? STATUS_CONFIG[statusModal.newStatus] : null;

  if (dbLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-teal-600 text-sm">Loading shipments from database...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex-1 min-w-48 flex items-center gap-2 bg-[#1a3333] border border-white/10 rounded-xl px-4 py-2.5">
          <i className="ri-search-line text-teal-400 text-sm" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by ID, sender, city..." className="flex-1 bg-transparent text-white text-sm outline-none placeholder-teal-700 min-w-0" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#1a3333] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-teal-200 outline-none cursor-pointer">
          <option value="">All Statuses</option>
          {(['picked_up','in_transit','out_for_delivery','hold','delivered'] as ShipmentStatus[]).map((s) => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
        </select>
        <select value={filterService} onChange={(e) => setFilterService(e.target.value)} className="bg-[#1a3333] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-teal-200 outline-none cursor-pointer">
          <option value="">All Services</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={loadShipments} className="px-4 py-2.5 rounded-xl text-sm text-teal-400 border border-teal-800 hover:bg-teal-900/30 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"><i className="ri-refresh-line" /> Refresh</button>

      </div>

      {/* Bulk bar */}
      {someSelected && (
        <div className="flex flex-wrap items-start gap-3 mb-4 bg-teal-900/30 border border-teal-700/40 rounded-2xl px-5 py-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-multiple-line text-teal-400 text-sm" /></div>
            <div><div className="text-white text-sm font-semibold">{selectedIds.size} selected</div><div className="text-teal-500 text-xs">Changes apply to all selected rows</div></div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(['picked_up','in_transit','out_for_delivery','hold','delivered'] as ShipmentStatus[]).map((opt) => {
              const oCfg = STATUS_CONFIG[opt];
              return (<button key={opt} onClick={() => setBulkStatus(opt)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${bulkStatus === opt ? `${oCfg.bg} ${oCfg.color} border-current/40` : 'border-teal-900/40 text-teal-600 hover:border-teal-700'}`}><i className={oCfg.icon} />{oCfg.label}</button>);
            })}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={applyBulkUpdate} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white cursor-pointer whitespace-nowrap" style={{ background: 'var(--primary)' }}><i className="ri-check-double-line" />Apply to {selectedIds.size}</button>
            <button onClick={() => setSelectedIds(new Set())} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-teal-500 hover:text-white cursor-pointer"><i className="ri-close-line" />Deselect</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#0d2020] rounded-2xl border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-4 w-10">
                  <button onClick={toggleSelectAll} className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${allFilteredSelected ? 'bg-teal-500 border-teal-500' : 'border-teal-800 hover:border-teal-600'}`}>
                    {allFilteredSelected && <i className="ri-check-line text-white text-xs" />}
                  </button>
                </th>
                {['Tracking ID','Sender','Receiver','Route','Service','Status','Location','Actions'].map((h) => (
                  <th key={h} className="text-left px-5 py-4 text-xs font-semibold text-teal-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((s) => {
                const cfg = STATUS_CONFIG[s.status];
                const isChecked = selectedIds.has(s.id);
                const isCopied = copiedId === s.id;
                return (
                  <tr key={s.id} className={`transition-colors ${isChecked ? 'bg-teal-900/20' : 'hover:bg-white/5'}`}>
                    <td className="px-4 py-4"><button onClick={() => toggleSelect(s.id)} className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${isChecked ? 'bg-teal-500 border-teal-500' : 'border-teal-800 hover:border-teal-600'}`}>{isChecked && <i className="ri-check-line text-white text-xs" />}</button></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setDetailShipment(s)}
                          className="font-mono text-xs text-teal-300 font-bold hover:text-white underline underline-offset-2 decoration-teal-700 hover:decoration-teal-400 cursor-pointer transition-colors"
                          title="View shipment details"
                        >
                          {s.id}
                        </button>
                        <button
                          onClick={() => handleCopyId(s.id)}
                          title={isCopied ? 'Copied!' : 'Copy tracking ID'}
                          className={`w-6 h-6 flex items-center justify-center rounded-md transition-all cursor-pointer flex-shrink-0 ${isCopied ? 'bg-teal-500/20 text-teal-300' : 'bg-white/5 text-teal-700 hover:text-teal-300 hover:bg-teal-900/40'}`}
                        >
                          <i className={`text-xs ${isCopied ? 'ri-check-line' : 'ri-file-copy-line'}`} />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-4"><div className="text-sm text-white">{s.sender.name}</div><div className="text-xs text-teal-600">{s.sender.city}</div></td>
                    <td className="px-5 py-4"><div className="text-sm text-white">{s.receiver.name}</div><div className="text-xs text-teal-600">{s.receiver.city}</div></td>
                    <td className="px-5 py-4"><div className="text-xs text-teal-300">{s.origin.city}</div><div className="text-xs text-teal-600">→ {s.destination.city}</div></td>
                    <td className="px-5 py-4"><span className="text-xs bg-teal-900/40 text-teal-300 px-2 py-1 rounded-md">{s.service}</span></td>
                    <td className="px-5 py-4"><span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color}`}><i className={cfg.icon} />{cfg.label}</span></td>
                    <td className="px-5 py-4">
                      {s.currentLocation?.city ? (
                        <span className="inline-flex items-center gap-1 text-xs text-teal-300 bg-teal-900/30 px-2 py-1 rounded-md">
                          <i className="ri-map-pin-2-fill text-teal-400" />{s.currentLocation.city}
                        </span>
                      ) : <span className="text-teal-800 text-xs">—</span>}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openStatusModal(s)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-900/30 hover:bg-teal-800/40 text-teal-400 hover:text-teal-300 transition-colors cursor-pointer"
                          title="Update Status"
                        >
                          <i className="ri-refresh-line text-sm" />
                        </button>
                        <button
                          onClick={() => setEditShipment(s)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-900/30 hover:bg-blue-900/30 text-teal-400 hover:text-sky-300 transition-colors cursor-pointer"
                          title="Edit Shipment"
                        >
                          <i className="ri-file-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => setTimelineShipment(s)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-900/30 hover:bg-teal-800/40 text-teal-400 hover:text-teal-300 transition-colors cursor-pointer"
                          title="Edit Timeline"
                        >
                          <i className="ri-git-branch-line text-sm" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(s)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-950/20 hover:bg-red-900/30 text-red-700 hover:text-red-400 transition-colors cursor-pointer"
                          title="Delete Shipment"
                        >
                          <i className="ri-delete-bin-6-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16 text-teal-600">
            <i className="ri-inbox-line text-3xl mb-3 block" />
            <p className="text-sm">{shipments.length === 0 ? 'No shipments yet. Create your first shipment from the Create Shipment tab.' : 'No shipments match your filters.'}</p>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {statusModal && modalCfg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.80)' }} onClick={(e) => { if (e.target === e.currentTarget) setStatusModal(null); }}>
          <div className="bg-[#0d2020] border border-teal-900/60 rounded-2xl w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5 flex-shrink-0">
              <div>
                <h3 className="font-heading font-semibold text-white text-base flex items-center gap-2"><i className="ri-edit-circle-line text-teal-400" />Update Shipment</h3>
                <p className="text-teal-600 text-xs mt-0.5 font-mono">{statusModal.shipment.id}</p>
              </div>
              <button onClick={() => setStatusModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-500 hover:text-white hover:bg-white/10 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-6 space-y-5">
              {/* Status selector */}
              <div>
                <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-3">New Status</label>
                <div className="grid grid-cols-1 gap-2">
                  {(['picked_up','in_transit','out_for_delivery','hold','delivered'] as ShipmentStatus[]).map((opt) => {
                    const oCfg = STATUS_CONFIG[opt];
                    const isSelected = statusModal.newStatus === opt;
                    return (
                      <button key={opt} type="button" onClick={() => setStatusModal((m) => m ? { ...m, newStatus: opt } : m)} className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer ${isSelected ? `${oCfg.bg} ${oCfg.color} border-current/50` : 'border-teal-900/30 bg-[#1a3333] text-teal-500 hover:border-teal-700'}`}>
                        <i className={`${oCfg.icon} text-sm`} /><span className="text-sm font-medium flex-1">{oCfg.label}</span>{isSelected && <i className="ri-check-line text-sm" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Current Location — Live map pin */}
              <div className="bg-[#0b1e1e] border border-teal-600/40 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-teal-400 text-sm" />
                  </div>
                  <div>
                    <div className="text-teal-300 text-xs font-semibold uppercase tracking-wide">Current Location</div>
                    <div className="text-teal-700 text-xs">Moves the live GPS pin on the customer tracking map</div>
                  </div>
                  {statusModal.currentLocation && (
                    <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-teal-400 bg-teal-900/40 border border-teal-700/40 rounded-full px-2.5 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-ping inline-block" />
                      LIVE
                    </span>
                  )}
                </div>
                <div className="relative">
                  <i className="ri-crosshair-2-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-500 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={statusModal.currentLocation}
                    onChange={(e) => setStatusModal((m) => m ? { ...m, currentLocation: e.target.value } : m)}
                    placeholder="e.g. Istanbul, Turkey  |  Dubai Airport, UAE  |  Frankfurt Hub, Germany"
                    className="w-full bg-[#1a3333] border border-teal-700/50 rounded-xl pl-10 pr-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500 transition-colors"
                  />
                </div>
                {statusModal.currentLocation ? (
                  <div className="mt-2.5 flex items-center gap-2 text-xs">
                    <i className="ri-check-double-line text-teal-400" />
                    <span className="text-teal-500">Map will show package at: </span>
                    <span className="text-white font-semibold">{statusModal.currentLocation}</span>
                  </div>
                ) : (
                  <p className="mt-2 text-teal-800 text-xs">Leave empty to keep the last known location on the map</p>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1">
                  Message to Customer
                  <span className="ml-2 text-teal-700 normal-case font-normal">(shown in tracking + emailed)</span>
                </label>
                {statusModal.shipment.receiver?.email ? (
                  <div className="flex items-center gap-1.5 mb-2 text-xs text-green-400 bg-green-900/20 border border-green-800/30 rounded-lg px-3 py-2">
                    <i className="ri-mail-send-line" />
                    Email notification will be sent to <strong>{statusModal.shipment.receiver.email}</strong>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 mb-2 text-xs text-teal-700 bg-teal-900/20 border border-teal-800/30 rounded-lg px-3 py-2">
                    <i className="ri-mail-close-line" />No receiver email on file — notification will not be sent
                  </div>
                )}
                <textarea
                  value={statusModal.reason}
                  onChange={(e) => setStatusModal((m) => m ? { ...m, reason: e.target.value } : m)}
                  placeholder="e.g. Your package has cleared customs and is now en route to the final delivery hub in your city."
                  rows={3}
                  maxLength={400}
                  className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500 resize-none"
                />
              </div>

              {/* Short note */}
              <div>
                <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1">Short Alert Note</label>
                <input type="text" value={statusModal.note} onChange={(e) => setStatusModal((m) => m ? { ...m, note: e.target.value } : m)} placeholder="e.g. Expected clearance in 2 business days" maxLength={150} className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
              </div>

              {/* Preview card */}
              {(statusModal.currentLocation || statusModal.reason) && (
                <div className="bg-teal-950/60 border border-teal-800/30 rounded-xl p-4">
                  <p className="text-teal-600 text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-1.5">
                    <i className="ri-eye-line" />
                    What the customer will see
                  </p>
                  <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border mb-3 ${modalCfg.bg} ${modalCfg.color}`}>
                    <i className={modalCfg.icon} />{modalCfg.label}
                  </div>
                  {statusModal.currentLocation && (
                    <div className="flex items-center gap-1.5 text-xs text-teal-300 mb-2">
                      <i className="ri-map-pin-2-fill text-teal-400" />
                      <span className="text-teal-600">Currently at:</span> <strong>{statusModal.currentLocation}</strong>
                    </div>
                  )}
                  {statusModal.reason && (
                    <p className="text-white/80 text-xs leading-relaxed">&ldquo;{statusModal.reason}&rdquo;</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 px-6 py-5 border-t border-white/5 flex-shrink-0">
              <button onClick={() => setStatusModal(null)} className="flex-1 py-3 rounded-xl text-sm font-semibold text-teal-400 border border-teal-900/40 hover:bg-teal-900/20 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={saveStatusUpdate} className="flex-1 py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap" style={{ background: 'var(--primary)' }}>
                <i className="ri-save-3-line" />Save &amp; Notify Customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Editor Modal */}
      {timelineShipment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={(e) => { if (e.target === e.currentTarget) setTimelineShipment(null); }}>
          <div className="bg-[#0d2020] border border-teal-900/60 rounded-2xl w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <div><h3 className="font-heading font-semibold text-white">Timeline Editor</h3><p className="text-teal-500 text-xs mt-0.5 font-mono">{timelineShipment.id}</p></div>
              <button onClick={() => setTimelineShipment(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-500 hover:text-white hover:bg-white/10 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {[...timelineShipment.timeline].reverse().map((evt, i) => {
                const eCfg = STATUS_CONFIG[evt.status];
                return (
                  <div key={i} className="flex gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border ${eCfg.bg} ${eCfg.color}`}><i className={`${eCfg.icon} text-xs`} /></div>
                    <div><div className={`font-semibold text-xs ${eCfg.color}`}>{eCfg.label}</div><div className="text-white text-xs">{evt.description}</div><div className="text-teal-600 text-xs">{evt.location} · {evt.timestamp}</div></div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-white/5 px-6 py-5 space-y-3">
              <p className="text-teal-400 text-xs font-semibold uppercase tracking-wide">Add New Event</p>
              <div className="grid grid-cols-2 gap-3">
                <select value={newEventStatus} onChange={(e) => setNewEventStatus(e.target.value as ShipmentStatus)} className="col-span-2 bg-[#1a3333] border border-teal-900/40 rounded-lg px-3 py-2.5 text-sm text-teal-200 outline-none cursor-pointer">
                  {(['picked_up','in_transit','out_for_delivery','hold','delivered'] as ShipmentStatus[]).map((s) => <option key={s} value={s} className="bg-[#0d2020]">{STATUS_CONFIG[s].label}</option>)}
                </select>
                <input value={newEventLocation} onChange={(e) => setNewEventLocation(e.target.value)} placeholder="City / Location" className="bg-[#1a3333] border border-teal-900/40 rounded-lg px-3 py-2.5 text-sm text-white outline-none placeholder-teal-700" />
                <input value={newEventDesc} onChange={(e) => setNewEventDesc(e.target.value)} placeholder="Description" className="bg-[#1a3333] border border-teal-900/40 rounded-lg px-3 py-2.5 text-sm text-white outline-none placeholder-teal-700" />
              </div>
              <button onClick={addTimelineEvent} disabled={!newEventLocation.trim() || !newEventDesc.trim()} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 cursor-pointer whitespace-nowrap" style={{ background: 'var(--primary)' }}>Add Event &amp; Update Map Location</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Shipment Modal */}
      {editShipment && (
        <AdminEditShipmentModal
          shipment={editShipment}
          onClose={() => setEditShipment(null)}
          onSaved={loadShipments}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}
        >
          <div className="bg-[#0d2020] border border-red-900/50 rounded-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-950/40 border border-red-800/30 flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-6-line text-red-400 text-lg" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-base">Delete Shipment</h3>
                <p className="text-red-400 text-xs mt-0.5">This action cannot be undone</p>
              </div>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-teal-300 text-sm">
                You are about to permanently delete shipment:
              </p>
              <div className="bg-[#1a3333] border border-teal-900/40 rounded-xl px-4 py-3 flex items-center gap-3">
                <i className="ri-archive-line text-teal-500" />
                <div>
                  <p className="font-mono text-sm text-white font-bold">{deleteTarget.id}</p>
                  <p className="text-xs text-teal-600 mt-0.5">
                    {deleteTarget.sender?.name} → {deleteTarget.receiver?.name} &nbsp;·&nbsp; {deleteTarget.origin?.city} → {deleteTarget.destination?.city}
                  </p>
                </div>
              </div>
              <p className="text-teal-600 text-xs">
                All associated data including timeline events, status history, and tracking information will be permanently removed from the database.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-5 border-t border-white/5">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-teal-400 border border-teal-900/40 hover:bg-teal-900/20 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white bg-red-700 hover:bg-red-600 disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deleting
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Deleting...</>
                  : <><i className="ri-delete-bin-6-line" />Delete Permanently</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Detail Side Panel */}
      <AdminShipmentDetailPanel
        shipment={detailShipment}
        onClose={() => setDetailShipment(null)}
        onEdit={(s) => { setDetailShipment(null); setEditShipment(s); }}
        onStatusUpdate={(s) => { setDetailShipment(null); openStatusModal(s); }}
      />
    </div>
  );
}